import React, { useMemo } from "react";
import {
  WSActions,
  WSElement,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { ContextItem, RequirementContextType } from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";
import { useQueryPayer } from "../../../../query/payers/queries/useQueryPayer";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { InsuranceCoverageMonitor } from "../../../../services/api/insurance/monitor/types";
import { InsuranceInstructionsView } from "../../../RequirementDefinitions/routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { selectorIsAcceptedSourceTypeIncluded } from "../../modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";

export type RequirementDetailsInstructionsProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction: () => void;
  onCloseAction: () => void;
  customContext?: ContextItem;
  requirement: IRequirementResponse;
  instructions: string;
  monitor?: InsuranceCoverageMonitor;
};

export const RequirementDetailsInstructions: React.FC<
  RequirementDetailsInstructionsProps
> = ({
  requirement,
  contextType,
  isLoading,
  getRequirementDetailsWidgets,
  getRequirementInstructionsWidgets,
  getRequirementInstructions,
  instructions,
  onSuccessAction,
  onCloseAction,
  monitor
}) => {
  const queryPayer = useQueryPayer(requirement.payerId, {
    enabled:
      requirement.requirementType === RequirementType.IdentityVerification
  });

  const requirementInstructionsWidgets =
    getRequirementInstructionsWidgets(requirement);
  const viewInstructions =
    getRequirementInstructions(requirement)?.(instructions);

  const payerName = useMemo(() => {
    return queryPayer.data
      ? wsName({
          member: queryPayer.data?.member,
          user: queryPayer.data?.user!
        }).getResolvedName()
      : "Payer";
  }, [queryPayer.data]);

  const acceptWingspanMasterPolicy = selectorIsAcceptedSourceTypeIncluded(
    "PlanEnrollment",
    monitor?.settings
  );

  return (
    <WSElement shimmer={isLoading}>
      <WSList gap="XL">
        <WSGrid>
          {requirementInstructionsWidgets ? null : (
            <WSGrid.Item span={{ s: "2" }} />
          )}
          <WSGrid.Item span={{ s: "8" }}>
            {requirement.requirementType !==
            RequirementType.InsuranceCoverage ? (
              <>
                <WSText.Paragraph weight="medium">
                  Requirement instructions
                </WSText.Paragraph>
                <WSText.ParagraphSm color="gray500" mt="S">
                  Review the details to ensure you have the necessary
                  information to complete the requirement.
                </WSText.ParagraphSm>

                <WSPanel mt="M" p="L">
                  {viewInstructions}

                  {requirement.requirementType ===
                  RequirementType.IdentityVerification ? (
                    <WSInfoBox shimmer={queryPayer.isLoading} mt="M">
                      {payerName} takes the threat of employment-related
                      identity theft seriously and requires all contractors to
                      undergo this verification process. {payerName} does not
                      collect, process, store, or access any biometric data
                      collected during the verification process, as defined by
                      applicable data privacy laws.
                    </WSInfoBox>
                  ) : null}
                </WSPanel>
              </>
            ) : (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={requirement.name || ""}
              />
            )}

            <WSActions
              mt="XL"
              alignment="fill"
              buttons={[
                {
                  label: "Continue",
                  kind: "Primary",
                  onClick: () => {
                    onSuccessAction();
                  }
                },
                {
                  label: "Cancel",
                  kind: "Secondary",
                  onClick: () => {
                    onCloseAction();
                  }
                }
              ]}
            ></WSActions>
          </WSGrid.Item>
          <WSGrid.Item span={{ s: "4" }}>
            {requirementInstructionsWidgets}
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
